import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PublicMenuTheme } from '../../shared/public-menu-theme';
import type {
  AdminBusinessDetail,
  AdminBusinessListItem,
  AdminAiChatFailureItem,
  BusinessAiEscalationListItem,
  BusinessIntelligence,
  BusinessOrderMetrics,
  BranchInventoryRow,
  DeliveryOrderDto,
  FulfillmentStage,
  InventoryAssignment,
  InventoryImportResult,
  InventoryMovement,
  InventoryStockAdjustmentResult,
  InventoryStockAlert,
  MenuAiMetadata,
  InventorySettings,
  InventoryTemplate,
  OrderView,
  PublicAiStatusResponse,
  PublicTenant,
  WaiterAccountDto,
  WaiterEmployeeDto,
  WaiterRoundResponse,
  WaiterTableDto,
} from '../../shared/types';
import { apiRequest, jsonBody } from '../lib/api';
import { queryKeys } from '../../lib/queryKeys';

export interface BusinessOverview {
  activeBranches: number;
  activeProducts: number;
  pendingOrders: number;
  preparingOrders: number;
  completedToday: number;
}

export interface BusinessUser {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'manager';
  active: boolean;
  createdAt: string;
}

export interface BusinessBranch {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  allowedRadiusM: number;
  requireGeolocation: boolean;
  whatsapp: string | null;
  correlationCurrent: number;
  correlationCycle: number;
  active: boolean;
  deliveryPrepMode: 'kitchen' | 'delivery';
}

export interface BusinessSettings {
  id: string;
  subdomain: string;
  name: string;
  description: string;
  logoKey: string | null;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  theme: PublicMenuTheme;
  localCurrency: string;
  currencySymbol: string;
  currencyDecimals: number | '';
  exchangeRate: number | '';
  whatsapp: string | null;
  timezone: string;
  inventoryLowStockThreshold: number;
  status: string;
}

export function useBusinessOverview() {
  return useQuery({
    queryKey: queryKeys.business.overview,
    queryFn: () => apiRequest<BusinessOverview>('/api/business/overview'),
  });
}

export function useBusinessOrderMetrics(date: string) {
  return useQuery({
    queryKey: queryKeys.business.orderMetrics(date),
    queryFn: () => apiRequest<BusinessOrderMetrics>(`/api/business/order-metrics?date=${encodeURIComponent(date)}`),
  });
}

export function useBusinessIntelligence(startDate: string, endDate: string, branchId?: string) {
  return useQuery({
    queryKey: queryKeys.business.intelligence(startDate, endDate, branchId),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ startDate, endDate });
      if (branchId) params.set('branchId', branchId);
      return apiRequest<BusinessIntelligence>(`/api/business/intelligence?${params}`, { signal });
    },
  });
}

export function useBusinessBranches(enabled = true) {
  return useQuery({
    queryKey: queryKeys.business.branches,
    queryFn: () => apiRequest<BusinessBranch[]>('/api/business/branches'),
    enabled,
  });
}

export function useBusinessAiEscalations(branchId = '', status = '') {
  return useQuery({
    queryKey: queryKeys.business.aiEscalations(branchId, status),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (branchId) params.set('branchId', branchId);
      if (status) params.set('status', status);
      const query = params.toString();
      return apiRequest<BusinessAiEscalationListItem[]>(`/api/business/ai-escalations${query ? `?${query}` : ''}`, { signal });
    },
  });
}

export function useBusinessUsers(enabled = true) {
  return useQuery({
    queryKey: queryKeys.business.users,
    queryFn: () => apiRequest<BusinessUser[]>('/api/business/users'),
    enabled,
  });
}

export function useBusinessWaiters() {
  return useQuery({
    queryKey: queryKeys.business.waiters,
    queryFn: () => apiRequest<WaiterEmployeeDto[]>('/api/business/waiters'),
  });
}

export function useInventoryTemplates() {
  return useQuery({
    queryKey: queryKeys.business.inventoryTemplates,
    queryFn: () => apiRequest<InventoryTemplate[]>('/api/business/inventory/templates'),
  });
}

export function useInventorySettings() {
  return useQuery({
    queryKey: queryKeys.business.inventorySettings,
    queryFn: () => apiRequest<InventorySettings>('/api/business/inventory/settings'),
  });
}

export function useInventoryAssignments() {
  return useQuery({
    queryKey: queryKeys.business.inventoryAssignments,
    queryFn: () => apiRequest<InventoryAssignment[]>('/api/business/inventory/assignments'),
  });
}

export function useBranchInventory(branchId: string) {
  return useQuery({
    queryKey: queryKeys.business.inventoryBranch(branchId),
    queryFn: () => apiRequest<BranchInventoryRow[]>(`/api/business/inventory/${branchId}`),
    enabled: Boolean(branchId),
  });
}

export function useInventoryMovements(branchId: string, productId: string, limit = 20) {
  return useQuery({
    queryKey: queryKeys.business.inventoryMovements(branchId, productId, limit),
    queryFn: () => apiRequest<InventoryMovement[]>(`/api/business/inventory/${branchId}/${productId}/movements?limit=${encodeURIComponent(String(limit))}`),
    enabled: Boolean(branchId && productId),
  });
}

export function useInventoryBranchMovements(branchId: string, limit = 5) {
  return useQuery({
    queryKey: queryKeys.business.inventoryBranchMovements(branchId, limit),
    queryFn: () => apiRequest<InventoryMovement[]>(`/api/business/inventory/${branchId}/movements?limit=${encodeURIComponent(String(limit))}`),
    enabled: Boolean(branchId),
  });
}

export function useInventoryAlerts(branchId = '', status: InventoryStockAlert['status'] | 'all' = 'activa') {
  return useQuery({
    queryKey: queryKeys.business.inventoryAlerts(branchId, status),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ status });
      if (branchId) params.set('branchId', branchId);
      return apiRequest<InventoryStockAlert[]>(`/api/business/inventory/alerts?${params}`, { signal });
    },
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
  });
}

export function useCreateInventoryTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => apiRequest<{ templateId: string }>('/api/business/inventory/templates', { method: 'POST', body: jsonBody(payload) }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryTemplates }); },
  });
}

export function useDuplicateInventoryTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, name }: { templateId: string; name?: string }) =>
      apiRequest<{ templateId: string }>(`/api/business/inventory/templates/${templateId}/duplicate`, { method: 'POST', body: jsonBody(name ? { name } : {}) }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryTemplates }); },
  });
}

export function useUpdateInventoryTemplateItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, items }: { templateId: string; items: Array<{ productId: string; stockQuantity: number }> }) =>
      apiRequest<{ templateId: string }>(`/api/business/inventory/templates/${templateId}/items`, { method: 'POST', body: jsonBody({ items }) }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryTemplates }); },
  });
}

export function useGenerateInventoryTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, branchId, date }: { templateId: string; branchId: string; date?: string }) =>
      apiRequest<{ templateId: string; itemsGenerated: number }>(`/api/business/inventory/templates/${templateId}/generate`, { method: 'POST', body: jsonBody({ branchId, date: date || undefined }) }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryTemplates }); },
  });
}

export function useUpsertInventoryAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { branchId: string; templateId: string; dayOfWeek: number }) =>
      apiRequest<{ assignmentId: string }>('/api/business/inventory/assignments', { method: 'PUT', body: jsonBody(payload) }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryAssignments }); },
  });
}

export function useBulkUpsertInventoryAssignments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { templateId: string; branchIds: string[]; dayOfWeeks: number[] }) =>
      apiRequest<{ updated: number }>('/api/business/inventory/assignments/bulk', { method: 'POST', body: jsonBody(payload) }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryAssignments }); },
  });
}

export function useDeleteInventoryAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) => apiRequest<{ assignmentId: string }>(`/api/business/inventory/assignments/${assignmentId}`, { method: 'DELETE' }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryAssignments }); },
  });
}

export function useUpdateBranchInventory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ branchId, productId, payload }: { branchId: string; productId: string; payload: Record<string, unknown> }) =>
      apiRequest<{ productId: string }>(`/api/business/inventory/${branchId}/${productId}`, { method: 'PUT', body: jsonBody(payload) }),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryBranch(variables.branchId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryAlerts(variables.branchId) });
    },
  });
}

export function useAdjustInventoryStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ branchId, productId, delta, note }: { branchId: string; productId: string; delta: number; note?: string }) =>
      apiRequest<InventoryStockAdjustmentResult>(`/api/business/inventory/${branchId}/${productId}/adjust`, { method: 'POST', body: jsonBody({ delta, note: note?.trim() || undefined }) }),
    onSuccess: async (result, variables) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryBranch(variables.branchId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryAlerts(variables.branchId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryMovements(variables.branchId, variables.productId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryBranchMovements(variables.branchId) });
      if (result.mode === 'template') {
        await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryTemplates });
        await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryAssignments });
      }
    },
  });
}

export function useImportInventory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ branchId, body, contentType }: { branchId: string; body: string; contentType: string }) =>
      apiRequest<InventoryImportResult>(`/api/business/inventory/import?branchId=${encodeURIComponent(branchId)}`, { method: 'POST', body, headers: { 'Content-Type': contentType } }),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryBranch(variables.branchId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryAlerts(variables.branchId) });
    },
  });
}

export function useUpdateInventorySettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: InventorySettings) => apiRequest<InventorySettings>('/api/business/inventory/settings', { method: 'PUT', body: jsonBody(payload) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventorySettings });
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.settings });
    },
  });
}

export function useCreateBusinessWaiter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => apiRequest<{ waiterId: string }>('/api/business/waiters', { method: 'POST', body: jsonBody(payload) }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: queryKeys.business.waiters }); },
  });
}

export function useUpdateBusinessWaiter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) => apiRequest<{ waiterId: string }>(`/api/business/waiters/${id}`, { method: 'PATCH', body: jsonBody(payload) }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: queryKeys.business.waiters }); },
  });
}

export function useBusinessTables(branchId = '') {
  return useQuery({
    queryKey: queryKeys.business.tables(branchId),
    queryFn: () => apiRequest<WaiterTableDto[]>(`/api/business/tables${branchId ? `?branchId=${encodeURIComponent(branchId)}` : ''}`),
    enabled: Boolean(branchId),
  });
}

export function useCreateBusinessTable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => apiRequest<{ tableId: string }>('/api/business/tables', { method: 'POST', body: jsonBody(payload) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.all });
      await queryClient.invalidateQueries({ queryKey: ['waiter'] });
    },
  });
}

export function useUpdateBusinessTable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) => apiRequest<{ tableId: string }>(`/api/business/tables/${id}`, { method: 'PATCH', body: jsonBody(payload) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.all });
      await queryClient.invalidateQueries({ queryKey: ['waiter'] });
    },
  });
}

export function useBusinessSettings() {
  return useQuery({
    queryKey: queryKeys.business.settings,
    queryFn: () => apiRequest<BusinessSettings>('/api/business/settings'),
  });
}

export function usePublicTenant() {
  return useQuery({
    queryKey: queryKeys.tenant.public(),
    queryFn: () => apiRequest<PublicTenant>('/api/public/tenant', { auth: false }),
  });
}

export function useWaiterTables(branchId = '') {
  return useQuery({
    queryKey: queryKeys.waiter.tables(branchId),
    queryFn: ({ signal }) => apiRequest<WaiterTableDto[]>(`/api/waiter/tables${branchId ? `?branchId=${encodeURIComponent(branchId)}` : ''}`, { signal }),
    enabled: Boolean(branchId),
  });
}

export function useWaiterAccount(accountId: string | null) {
  return useQuery({
    queryKey: queryKeys.waiter.account(accountId ?? 'none'),
    queryFn: ({ signal }) => apiRequest<WaiterAccountDto>(`/api/waiter/accounts/${encodeURIComponent(accountId ?? '')}`, { signal }),
    enabled: Boolean(accountId),
  });
}

export function useOpenWaiterAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tableId: string) => apiRequest<WaiterAccountDto>('/api/waiter/accounts', { method: 'POST', body: jsonBody({ tableId }) }),
    onSuccess: async (account) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.waiter.tables(account.branchId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.waiter.account(account.id) }),
      ]);
    },
  });
}

export function useSubmitWaiterRound() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => apiRequest<WaiterRoundResponse>('/api/waiter/rounds', { method: 'POST', body: jsonBody(payload) }),
    onSuccess: async (round) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.waiter.account(round.accountId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all }),
      ]);
    },
  });
}

export function usePublicAiStatus(branchId: string) {
  return useQuery({
    queryKey: queryKeys.publicAi.status(branchId),
    queryFn: ({ signal }) => apiRequest<PublicAiStatusResponse>(
      `/api/public/ai/status?branchId=${encodeURIComponent(branchId)}`,
      { auth: false, signal },
    ),
    enabled: Boolean(branchId),
    staleTime: 60_000,
    retry: false,
  });
}

export interface AdminOverview {
  businesses: number;
  activeBranches: number;
  activeOrders: number;
  activeBusinessUsers: number;
}

export function useAdminOverview() {
  return useQuery({
    queryKey: queryKeys.admin.overview,
    queryFn: () => apiRequest<AdminOverview>('/api/admin/overview'),
  });
}

export function useAdminBusinesses(search = '', status = '') {
  return useQuery({
    queryKey: queryKeys.admin.businesses(search, status),
    queryFn: async () => {
      const query = new URLSearchParams();
      if (search) query.set('search', search);
      if (status) query.set('status', status);
      return apiRequest<AdminBusinessListItem[]>(`/api/admin/businesses?${query}`);
    },
  });
}

export function useAdminBusinessDetail(businessId: string | null) {
  return useQuery({
    queryKey: queryKeys.admin.businessDetail(businessId ?? 'none'),
    queryFn: async () => {
      if (!businessId) throw new Error('Business id is required');
      return apiRequest<AdminBusinessDetail>(`/api/admin/businesses/${businessId}`);
    },
    enabled: Boolean(businessId),
  });
}

export function useAdminAiChatFailures(status = 'active') {
  return useQuery({
    queryKey: queryKeys.admin.aiChatFailures(status),
    queryFn: ({ signal }) => apiRequest<AdminAiChatFailureItem[]>(`/api/admin/ai-chat-failures?status=${encodeURIComponent(status)}`, { signal }),
  });
}

export interface MenuCatalogData {
  categories: Array<{ id: string; name: string; description: string; imageKey: string | null; imageUrl: string | null; order: number; active: boolean }>;
  products: Array<Record<string, unknown>>;
  optionGroups: Array<Record<string, unknown>>;
  branches: Array<{ id: string; name: string; active: boolean }>;
}

export function useBusinessMenuData() {
  return useQuery({
    queryKey: queryKeys.menu.catalog,
    queryFn: async () => {
      const [categories, products, optionGroups, branches] = await Promise.all([
        apiRequest<MenuCatalogData['categories']>('/api/business/categories'),
        apiRequest<MenuCatalogData['products']>('/api/business/products'),
        apiRequest<MenuCatalogData['optionGroups']>('/api/business/option-groups'),
        apiRequest<MenuCatalogData['branches']>('/api/business/branches'),
      ]);
      return { categories, products, optionGroups, branches };
    },
  });
}

export function useBulkUpdateProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { productIds: string[]; active?: boolean; categoryId?: string | null; preparationZone?: string }) =>
      apiRequest<{ updated: number }>('/api/business/products/bulk', { method: 'PUT', body: jsonBody(payload) }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.catalog }),
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.products }),
      ]);
    },
  });
}

export function useReorderCategories() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { items: Array<{ id: string; order: number }> }) =>
      apiRequest<{ updated: number }>('/api/business/categories/order', { method: 'PUT', body: jsonBody(payload) }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.catalog }),
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.categories }),
      ]);
    },
  });
}

export function useReorderProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { items: Array<{ id: string; order: number }> }) =>
      apiRequest<{ updated: number }>('/api/business/products/order', { method: 'PUT', body: jsonBody(payload) }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.catalog }),
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.products }),
      ]);
    },
  });
}

export function useGenerateProductAiMetadata() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { productIds: string[] }) =>
      apiRequest<{ updated: number; suggestions: Array<{ productId: string; aiMetadata: MenuAiMetadata }> }>('/api/business/products/ai-metadata-suggestions', { method: 'PUT', body: jsonBody(payload) }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.catalog }),
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.products }),
      ]);
    },
  });
}

export function useCreateBusinessBranch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => apiRequest<BusinessBranch>('/api/business/branches', {
      method: 'POST',
      body: jsonBody(payload),
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.branches });
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.overview });
    },
  });
}

export function useUpdateBusinessBranch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) => apiRequest<BusinessBranch>(`/api/business/branches/${id}`, {
      method: 'PATCH',
      body: jsonBody(payload),
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.branches });
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.overview });
    },
  });
}

export function useResetBusinessBranchCorrelation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, confirmation }: { id: string; confirmation: string }) => apiRequest<BusinessBranch>(`/api/business/branches/${id}/reset-correlation`, {
      method: 'POST',
      body: jsonBody({ confirmation }),
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.branches });
    },
  });
}

export function useCreateBusinessUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => apiRequest<BusinessUser>('/api/business/users', {
      method: 'POST',
      body: jsonBody(payload),
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.users });
    },
  });
}

export function useUpdateBusinessUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) => apiRequest<BusinessUser>(`/api/business/users/${id}`, {
      method: 'PATCH',
      body: jsonBody(payload),
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.users });
    },
  });
}

export function useSaveBusinessSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload }: { payload: Record<string, unknown> }) => apiRequest('/api/business/settings', {
      method: 'PUT',
      body: jsonBody(payload),
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.settings });
    },
  });
}

export function useSaveBusinessTheme() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ theme }: { theme: PublicMenuTheme }) => apiRequest<PublicMenuTheme>('/api/business/theme', {
      method: 'PUT',
      body: jsonBody(theme),
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.settings });
    },
  });
}

export function useResetBusinessTheme() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiRequest<PublicMenuTheme>('/api/business/theme', { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.settings });
    },
  });
}

export function useOrders(branchId: string, status: string) {
  return useQuery({
    queryKey: queryKeys.orders.list(branchId, status),
    queryFn: ({ signal }) =>
      apiRequest<OrderView[]>(`/api/orders?branchId=${encodeURIComponent(branchId)}&status=${status}`, { signal }),
    enabled: Boolean(branchId),
    staleTime: 5_000,
  });
}

interface FulfillmentMutationInput {
  orderId: string;
  expectedStage: FulfillmentStage;
  mutationId?: string;
}

function mutationId(value?: string): string {
  return value ?? crypto.randomUUID();
}

export function useOrderDetail(orderId: string | null) {
  return useQuery({
    queryKey: queryKeys.orders.detail(orderId ?? 'none'),
    queryFn: ({ signal }) => apiRequest<OrderView>(`/api/orders/${encodeURIComponent(orderId ?? '')}`, { signal }),
    enabled: Boolean(orderId),
  });
}

export function useConfirmDeliveryLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, expectedStage, mapsUrl, mutationId: id }: FulfillmentMutationInput & { mapsUrl: string }) =>
      apiRequest<{ outcome: string; stage: FulfillmentStage; confirmedMapsUrl: string }>(`/api/orders/${orderId}/fulfillment/location`, {
        method: 'POST',
        body: jsonBody({ expectedStage, mapsUrl, mutationId: mutationId(id) }),
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(variables.orderId) }),
      ]);
    },
  });
}

export function useDeliveryTokenMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, expectedStage, action, mutationId: id }: FulfillmentMutationInput & { action: 'issue' | 'rotate' | 'revoke' }) =>
      apiRequest<{ outcome: string; tokenId: string | null; stage: FulfillmentStage; secretAvailable: boolean; credential?: { rawToken: string } }>(`/api/orders/${orderId}/fulfillment/token`, {
        method: 'POST',
        body: jsonBody({ expectedStage, action, mutationId: mutationId(id) }),
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(variables.orderId) }),
      ]);
    },
  });
}

export function useFulfillmentHandoff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, expectedStage, mutationId: id }: FulfillmentMutationInput) =>
      apiRequest<{ outcome: string; stage: FulfillmentStage }>(`/api/orders/${orderId}/fulfillment/handoff`, {
        method: 'POST',
        body: jsonBody({ action: 'handoff', expectedStage, mutationId: mutationId(id) }),
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(variables.orderId) }),
      ]);
    },
  });
}

export function useFulfillmentDeliveryComplete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, expectedStage, mutationId: id }: FulfillmentMutationInput) =>
      apiRequest<{ outcome: string; stage: FulfillmentStage }>(`/api/orders/${orderId}/fulfillment/deliver`, {
        method: 'POST',
        body: jsonBody({ action: 'deliver', expectedStage, mutationId: mutationId(id) }),
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(variables.orderId) }),
      ]);
    },
  });
}

export function useFulfillmentCorrection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, expectedStage, reason, mutationId: id }: FulfillmentMutationInput & { reason: string }) =>
      apiRequest<{ outcome: string; stage: FulfillmentStage }>(`/api/orders/${orderId}/fulfillment/correction`, {
        method: 'POST',
        body: jsonBody({ action: 'correct', targetStage: 'ready', expectedStage, reason, mutationId: mutationId(id) }),
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(variables.orderId) }),
      ]);
    },
  });
}

export function useLateFulfillmentCancellation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, expectedStage, reason, mutationId: id }: FulfillmentMutationInput & { reason: string }) =>
      apiRequest<{ outcome: string; stage: FulfillmentStage }>(`/api/orders/${orderId}/fulfillment/cancel`, {
        method: 'POST',
        body: jsonBody({ action: 'cancel', expectedStage, reason, mutationId: mutationId(id) }),
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(variables.orderId) }),
      ]);
    },
  });
}

export function useDeliveryOrder(token: string) {
  return useQuery({
    queryKey: queryKeys.delivery.order(token),
    queryFn: ({ signal }) => apiRequest<DeliveryOrderDto>(`/api/delivery/${encodeURIComponent(token)}`, { auth: false, signal }),
    enabled: Boolean(token),
    retry: 1,
  });
}

export function useDeliveryTransition(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ action, expectedStage, mutationId: id }: { action: 'dispatch' | 'deliver'; expectedStage: FulfillmentStage; mutationId?: string }) =>
      apiRequest<{ outcome: string; stage: FulfillmentStage }>(`/api/delivery/${encodeURIComponent(token)}/${action}`, {
        auth: false,
        method: 'POST',
        body: jsonBody({ action, expectedStage, mutationId: mutationId(id) }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.delivery.order(token) });
    },
  });
}

export function useKitchenOrders(branchId: string, zone: string) {
  const status = 'preparacion';
  const zoneParam = zone ? `&zone=${encodeURIComponent(zone)}` : '';
  return useQuery({
    queryKey: queryKeys.orders.list(branchId, status + zoneParam),
    queryFn: ({ signal }) =>
      apiRequest<OrderView[]>(`/api/orders?branchId=${encodeURIComponent(branchId)}&status=${status}${zoneParam}`, { signal }),
    enabled: Boolean(branchId),
    staleTime: 5_000,
  });
}

export function useKitchenZones(branchId: string) {
  return useQuery({
    queryKey: queryKeys.kitchen.zones(branchId),
    queryFn: ({ signal }) =>
      apiRequest<string[]>(`/api/orders/zones?branchId=${encodeURIComponent(branchId)}`, { signal }),
    enabled: Boolean(branchId),
    staleTime: 60_000,
  });
}

export interface PublicOrderStatus {
  orderId: string;
  correlation: number;
  status: 'pendiente' | 'preparacion' | 'completado' | 'cancelado' | 'fantasma';
  fulfillmentStage: FulfillmentStage | null;
  totalUsdCents: number;
  totalLocalMinor: number;
  localCurrency: string;
  currencySymbol: string;
  currencyDecimals: number;
  createdAt: string;
  updatedAt: string;
}

export function usePublicOrderStatus(token: string) {
  return useQuery({
    queryKey: queryKeys.publicOrders.status(token),
    queryFn: ({ signal }) =>
      apiRequest<PublicOrderStatus>(`/api/public/orders/${encodeURIComponent(token)}`, { auth: false, signal }),
    refetchInterval: (query) => {
      const order = query.state.data as PublicOrderStatus | undefined;
      if (order?.status === 'completado' || order?.status === 'cancelado' || order?.fulfillmentStage === 'delivered' || order?.fulfillmentStage === 'handed_off' || order?.fulfillmentStage === 'cancelled') {
        return false;
      }
      return 60_000;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
    retry: 1,
  });
}

export function useCreateOptionGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiRequest<{ groupId: string }>('/api/business/option-groups', { method: 'POST', body: jsonBody(payload) }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.catalog }),
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.optionGroups }),
      ]);
    },
  });
}

export function useUpdateOptionGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, payload }: { groupId: string; payload: Record<string, unknown> }) =>
      apiRequest(`/api/business/option-groups/${groupId}`, { method: 'PATCH', body: jsonBody(payload) }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.catalog }),
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.optionGroups }),
      ]);
    },
  });
}

export function useDeactivateOptionGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) =>
      apiRequest(`/api/business/option-groups/${groupId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.catalog }),
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.optionGroups }),
      ]);
    },
  });
}
